import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  EventEmitter,
  OnDestroy,
  Renderer2,
  ContentChild,
  OnChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SelectDropdownIconDirective } from '@purespectrum1/ui/select-dropdown';
import {
  TreeDropdownOption,
  DropdownLabelAlignment,
  SelectedFile,
} from '../../interfaces/tree-dropdown-interface';

@Component({
  selector: 'ps-ui-tree-dropdown',
  templateUrl: './tree-dropdown.component.html',
  styleUrls: ['./tree-dropdown.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TreeDropdownComponent,
      multi: true,
    },
  ],
})
export class TreeDropdownComponent<T extends { [key: string]: any }>
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  private _dropdownItems: T[] = [];
  private _outsideClickListener?: () => void;
  private _selected: T = {} as T;

  @Input() set dropdownItems(value: T[]) {
    this._dropdownItems = value;
    //Whenever dropdown Items update, we need to make sure to update parent and child values
    this._checkAndSelectParent();
  }
  get dropdownItems(): T[] {
    return this._dropdownItems;
  }
  @Input() label: string = '';
  @Input() labelNone: string = 'Select';
  @Input() disabled: boolean = false;
  @Input() labelAlignment: DropdownLabelAlignment = 'center';
  @Input() enableSearch: boolean = true;
  @Input() clearSearch: boolean = true;
  @Input() isRemoveSelection: boolean = false;
  @Input() fileOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    removeItem: true,
    noContentMsg: '',
    icon: '',
    mcIconIdentifier: '',
  };
  @Input() folderOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    navigationKey: 'children',
    removeItem: true,
    noContentMsg: '',
    headerLabel: '',
    icon: '',
    mcIconIdentifier: '',
  };
  @Input() footerInputPlaceholder: string = 'New List';
  @Input() footerButtonText: string = 'New List';
  @Input() showFooterSelectButton: boolean = true;
  @Input() footerSelectButtonText: string = 'Select Folder';
  @Input() maxDepth = 2;
  @Input() addNewFolder = false;
  @Input() multi = false;
  @Input() showFolderArrowIcon = false;
  @Input() isFirstFooterButton = true;
  @Input() indicator = false;

  @Output() readonly selectFile = new EventEmitter();
  @Output() readonly selectFolder = new EventEmitter();
  @Output() readonly deletedParent = new EventEmitter();
  @Output() readonly deletedChild = new EventEmitter();
  @Output() readonly removeSelection = new EventEmitter();
  @Output() readonly deleteTemplateItem = new EventEmitter();
  @Output() readonly saveFolderName = new EventEmitter();
  @Output() readonly selectedFiles = new EventEmitter();

  @ViewChild('toggleButton') toggleButton!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('folderInput') set folderNameInput(content: ElementRef) {
    if (content) {
      // initially setter gets called with undefined
      content.nativeElement.focus();
    }
  }

  @ContentChild(SelectDropdownIconDirective, { static: true })
  selectIcon?: SelectDropdownIconDirective;

  public footerInputMode = false;

  public isOpen: boolean = false;
  public childList: T[] = [];
  public selectedFolder: T = {} as T;
  public selectedFile: T = {} as T;
  public searchText = new FormControl('');
  public previousSelectedFolders: T[] = [];
  public selectionLength = 0;
  inputItem = new FormControl('');
  public onChange: (item: T) => void = () => void 0;

  constructor(private _renderer: Renderer2, private _elementRef: ElementRef) {}

  ngOnInit(): void {
    this._listenToOutsideClicks();
  }

  ngOnChanges() {
    this._verifySelectedLength();
  }

  ngOnDestroy(): void {
    if (this._outsideClickListener) {
      this._outsideClickListener();
    }
  }

  get textAlignment() {
    return `text-${this.labelAlignment}`;
  }

  get background() {
    if (!this.isOpen) {
      return '';
    }

    return 'dropdown-button-open';
  }

  get icon() {
    return !!this.selectIcon;
  }

  public toggle() {
    this.isOpen = !this.isOpen;
    // focus on searchBox when dropdown is opened
    if (this.isOpen) {
      this._focusSearchInput();
    } else {
      this.toggleButton.nativeElement.focus();
    }
  }

  public open() {
    this.isOpen = true;
  }

  public close() {
    this.isOpen = false;
  }

  public getLabel() {
    if (this.label) {
      return this.label;
    }
    return this.selectedFile[this.fileOption.displayKey] || this.labelNone;
  }

  public getFolderNavigationKey() {
    return this.folderOption.navigationKey || '';
  }

  public getFileNavigationKey() {
    return this.fileOption.navigationKey || '';
  }

  get isRootFolder(): boolean {
    return !this.selectedFolder[this.folderOption.uniqueKey];
  }

  get folderItems(): T[] {
    return !this.isRootFolder
      ? this.selectedFolder[this.getFolderNavigationKey()] || []
      : this.dropdownItems;
  }

  get fileItems(): T[] {
    return !this.isRootFolder
      ? this.selectedFolder[this.getFileNavigationKey()] || []
      : [];
  }

  get selectedParent() {
    const length = this.previousSelectedFolders.length;
    return this.previousSelectedFolders[length - 1] || <T>{};
  }

  get currentDepth() {
    return this.previousSelectedFolders.length;
  }

  get noContentMessage() {
    if (this.searchText.value) {
      return 'No Result';
    }
    if (this.isRootFolder || !this.fileOption.noContentMsg) {
      return this.folderOption.noContentMsg;
    }
    return this.fileOption.noContentMsg;
  }

  // Getter-Setter for Selected Tree item
  get selected(): T {
    return this._selected;
  }

  set selected(item: T) {
    this._selected = {
      ...this.selectedFolder,
      [this.getFileNavigationKey()]: Array.isArray(item) ? item : [item],
    };
    this.selectFile.emit(this._selected);
    this.onChange(this._selected);
  }

  public selectFileItem(item: T) {
    if (!this.multi) {
      this.selected = item;
      this.close();
    }
  }

  // When item is attempted to be selected with the help of keyboard keys
  public keydown(e: KeyboardEvent, item: T) {
    switch (e.key) {
      case 'Enter':
        this.selectFileItem(item);
        break;
      case ' ':
        this.selectFileItem(item);
        break;
      case 'Escape':
        break;
    }
  }

  private _verifySelectedLength() {
    this.selectionLength = this.dropdownItems.reduce((acc, next) => {
      return (
        acc +
        next[this.fileOption.navigationKey!]?.filter(
          (template: any) => template.isChecked
        ).length
      );
    }, 0);
  }

  private _checkAndSelectParent() {
    if (
      this.selectedFolder &&
      this.selectedFolder[this.folderOption.uniqueKey]
    ) {
      this.selectFolderItem(this.selectedFolder);
    }
  }

  public selectFolderItem(item: T, e?: Event) {
    this.previousSelectedFolders.push(this.selectedFolder);
    this.selectedFolder =
      this.folderItems.find(
        (i) =>
          i[this.folderOption.uniqueKey] === item[this.folderOption.uniqueKey]
      ) || ({} as T);
    this.searchText.setValue('');
    this.selectFolder.emit({
      folder: this.selectedFolder,
      parent: this.selectedParent,
    });
    if (this.currentDepth > this.maxDepth) {
      this.close();
    }
    if (e) {
      e.stopPropagation();
    }
  }

  public goBack(e: Event) {
    this.selectedFolder = this.previousSelectedFolders.pop() || <T>{};
    this.selectFolder.emit({
      folder: this.selectedFolder,
      parent: this.selectedParent,
    });
    this.searchText.setValue('');
    e.stopPropagation();
  }

  public remove(e: Event) {
    this.removeSelection.emit();
    e.stopPropagation();
  }

  public deleteFolder(i: number, e: Event) {
    this.deletedParent.emit(this.folderItems[i][this.folderOption.uniqueKey]);
    e.stopPropagation();
  }

  public deleteFile(i: number, e: Event) {
    this.deletedChild.emit(this.fileItems[i][this.fileOption.uniqueKey]);
    e.stopPropagation();
  }

  public deleteTemplate(item: TreeDropdownOption, e: Event, type: string) {
    item.type = type;
    this.previousSelectedFolders = [];
    this.deleteTemplateItem.emit(item);
    e.stopPropagation();
  }

  public emptySearch() {
    this.searchText.setValue('');
    this._focusSearchInput();
  }

  private _focusSearchInput() {
    let el: ElementRef | null = null;
    if (this.enableSearch) {
      el = this.searchInput;
    }
    if (el) {
      setTimeout(() => {
        el?.nativeElement.focus();
      }, 0);
    }
  }

  // Deciding what element to focus when keydown event fires
  public focusItems(
    e: KeyboardEvent,
    isToggleButton?: boolean,
    upClose?: boolean
  ) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (isToggleButton && !this.isOpen) {
        // open dropdown when press down and toggle button is focused
        this.open();
      }
    } else if (e.key === 'Escape') {
      this.closeFocus();
    } else if (e.key === 'ArrowUp') {
      if (isToggleButton || upClose) {
        // close dropdown when press up and toggle button is focused
        this.closeFocus();
        return;
      }
      const activeEl = document.activeElement;
      const searchInput = this.searchInput;
      if (searchInput.nativeElement === activeEl) {
        this.toggleButton.nativeElement.focus();
      }
    }
  }

  closeFocus() {
    this.close();
    this.toggleButton.nativeElement.focus();
  }

  private _listenToOutsideClicks() {
    this._outsideClickListener = this._renderer.listen(
      'document',
      'click',
      (event: Event) => {
        const attrId = (event.target as HTMLElement).id;
        if (
          !this._elementRef.nativeElement.contains(event.target) &&
          ![
            'footer-button-check',
            'footer-button',
            'new-input-remove',
            'new-input-list',
          ].includes(attrId)
        ) {
          {
            this.close();
          }
        }
      }
    );
  }

  // Control Value Accessor implementation
  writeValue(obj: T): void {
    this._selected = obj || {};
    if (this._selected && this._selected[this.getFileNavigationKey()]) {
      this.selectedFolder =
        this.findSelectedParent(this.dropdownItems, this._selected) || <T>{};
      this.selectedFile = this._selected[this.getFileNavigationKey()][0];
    } else {
      this.selectedFile = this.selectedFolder = {} as T;
      this.previousSelectedFolders = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    // do nothing. Needed for ControlValueAccessor interface
  }

  findSelectedParent(items: T[], selectedItem: T): T | undefined {
    let selectedFolder = (items || []).find((i) => {
      return (
        i[this.folderOption.uniqueKey] ===
        selectedItem[this.folderOption.uniqueKey]
      );
    });
    if (selectedFolder) {
      return selectedFolder;
    }
    (items || []).some((i) => {
      selectedFolder = this.findSelectedParent(
        i[this.getFolderNavigationKey()],
        selectedItem
      );
      return selectedFolder;
    });
    return selectedFolder;
  }

  public onSaveFolderName(e: Event) {
    if (!this.inputItem.value) {
      return;
    }
    this.saveFolderName.emit(this.inputItem.value);
    this.footerInputMode = false;
    this.inputItem.setValue('');
    e.stopPropagation();
  }

  public removeEmptySpace() {
    const regx = /^\s+$/;
    if (regx.test(this.inputItem.value)) {
      return this.inputItem.setValue(this.inputItem.value.replace(regx, ''));
    }
  }

  public onSelectFolder() {
    if (!this.isRootFolder || this.multi) {
      this.close();
      if (this.multi) {
        this._emitSelectedFiles();
      }
    }
  }

  public selectFiles(
    item: T & { isChecked?: boolean },
    type: string,
    e: Event
  ) {
    const isChecked = (<HTMLInputElement>e.target).checked;
    if (type === 'file') {
      item.isChecked = isChecked;
    } else if (type === 'folder') {
      item.templates?.map((template: T) =>
        this.selectFiles(template, 'file', e)
      );
      item.subfolders?.map((folder: T) =>
        this.selectFiles(folder, 'folder', e)
      );
    }
  }

  public isFolderChecked(item: T) {
    return (
      item.templates?.some((template: T) => template.isChecked) ||
      item.subfolders?.some((folder: T) => this.isFolderChecked(folder))
    );
  }

  private _emitSelectedFiles() {
    const selectedFiles: SelectedFile[] = [];
    this.dropdownItems.forEach((dropdownItem: T) => {
      selectedFiles.push(...this._getFiles(dropdownItem.templates));
      if (dropdownItem.subfolders) {
        dropdownItem.subfolders.forEach((subfolder: T) =>
          selectedFiles.push(...this._getFiles(subfolder.templates))
        );
      }
    });

    this.selectionLength = selectedFiles.length;
    this.selectedFiles.emit(selectedFiles);
  }

  private _getFiles(templates: T[] = []) {
    return templates
      .filter((template: T) => !!template.isChecked)
      .map((template: T) => {
        return {
          id: template.id,
          name: template.name,
        };
      });
  }
}
